// Local-first upload/tracking rules.
//
// Every change writes here synchronously so the native upload gate and the
// settings UI both see the latest user intent immediately, even when offline.
// A `dirty` flag + `updatedAt` timestamp let the reconcile worker push
// pending changes to Supabase on next reconnect. Last-write-wins from THIS
// device per project policy.

import { kv } from "@/lib/native/kv";
import { useEffect, useState } from "react";
import { getDeviceId } from "./device-id";

const KEY = "roava.upload_rules";
const EVENT = "roava:upload-rules-changed";

export interface UploadRules {
  wifiOnly: boolean;
  uploadOriginals: boolean;
  compressedPreviewsFirst: boolean;
  uploadMode: "auto" | "manual";
  minBatteryPct: number;
  requireChargingUnderMin: boolean;
}

export interface StoredUploadRules extends UploadRules {
  /** ISO timestamp of last local edit. */
  updatedAt: string;
  /** "1" if local changes haven't been pushed to server yet. */
  dirty: boolean;
  /** Device that produced this local edit. */
  deviceId: string | null;
  /** Trip ID these rules are scoped to (rules are per-trip server-side). */
  tripId: string | null;
}

export const DEFAULT_RULES: UploadRules = {
  wifiOnly: true,
  uploadOriginals: true,
  compressedPreviewsFirst: false,
  uploadMode: "auto",
  minBatteryPct: 20,
  requireChargingUnderMin: true,
};

let mem: StoredUploadRules | null = null;
let hydrated = false;
let hydrating: Promise<StoredUploadRules | null> | null = null;

function emit(s: StoredUploadRules) {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(EVENT, { detail: s }));
  }
}

function fillDefaults(partial: Partial<StoredUploadRules> | null, tripId: string | null): StoredUploadRules {
  return {
    ...DEFAULT_RULES,
    ...(partial ?? {}),
    tripId: partial?.tripId ?? tripId,
    updatedAt: partial?.updatedAt ?? new Date(0).toISOString(),
    dirty: partial?.dirty ?? false,
    deviceId: partial?.deviceId ?? null,
  };
}

export async function hydrateLocalUploadRules(): Promise<StoredUploadRules | null> {
  if (hydrated) return mem;
  if (hydrating) return hydrating;
  hydrating = (async () => {
    try {
      const raw = await kv.get(KEY);
      if (raw) mem = JSON.parse(raw) as StoredUploadRules;
    } catch {
      /* ignore */
    }
    hydrated = true;
    return mem;
  })();
  try {
    return await hydrating;
  } finally {
    hydrating = null;
  }
}

export function getLocalUploadRulesSync(tripId: string | null = null): StoredUploadRules {
  return fillDefaults(mem, tripId);
}

export async function getLocalUploadRules(tripId: string | null = null): Promise<StoredUploadRules> {
  if (!hydrated) await hydrateLocalUploadRules();
  return fillDefaults(mem, tripId);
}

/**
 * Apply a partial rules change locally and mark dirty.
 * `tripId` is required so the reconcile worker knows where to push.
 */
export async function setLocalUploadRules(
  patch: Partial<UploadRules>,
  tripId: string,
): Promise<StoredUploadRules> {
  await hydrateLocalUploadRules();
  const deviceId = await getDeviceId();
  const next: StoredUploadRules = {
    ...fillDefaults(mem, tripId),
    ...patch,
    tripId,
    updatedAt: new Date().toISOString(),
    dirty: true,
    deviceId,
  };
  mem = next;
  hydrated = true;
  try {
    await kv.set(KEY, JSON.stringify(next));
  } catch {
    /* ignore quota errors */
  }
  emit(next);
  return next;
}

/**
 * Mirror server-fetched rules into the local cache WITHOUT marking dirty.
 * Used after a successful pull or a successful push.
 * Skips the write if local copy is dirty AND newer than server's mirror time.
 */
export async function mirrorServerUploadRules(
  serverRules: UploadRules,
  tripId: string,
): Promise<StoredUploadRules> {
  await hydrateLocalUploadRules();
  if (mem?.dirty && mem.tripId === tripId) {
    // Preserve local edits — settings-sync will push them on next reconcile.
    return mem;
  }
  const next: StoredUploadRules = {
    ...DEFAULT_RULES,
    ...serverRules,
    tripId,
    updatedAt: new Date().toISOString(),
    dirty: false,
    deviceId: mem?.deviceId ?? null,
  };
  mem = next;
  hydrated = true;
  try {
    await kv.set(KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
  emit(next);
  return next;
}

/** Clear the dirty flag once the server has accepted our push. */
export async function markUploadRulesSynced(tripId: string): Promise<void> {
  await hydrateLocalUploadRules();
  if (!mem || mem.tripId !== tripId) return;
  const next: StoredUploadRules = { ...mem, dirty: false };
  mem = next;
  try {
    await kv.set(KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
  emit(next);
}

/** React hook. */
export function useLocalUploadRules(tripId: string | null = null): StoredUploadRules {
  const [s, setS] = useState<StoredUploadRules>(() => fillDefaults(mem, tripId));
  useEffect(() => {
    let alive = true;
    void hydrateLocalUploadRules().then(() => { if (alive) setS(fillDefaults(mem, tripId)); });
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<StoredUploadRules>).detail;
      if (detail) setS(detail);
    };
    if (typeof window !== "undefined") {
      window.addEventListener(EVENT, onChange);
    }
    return () => {
      alive = false;
      if (typeof window !== "undefined") {
        window.removeEventListener(EVENT, onChange);
      }
    };
  }, [tripId]);
  return s;
}
